import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LanguageToggle from '../LanguageToggle';
import BottomSheet from './BottomSheet';

/**
 * Student profile bottom sheet — opened by the avatar of the student's Figma
 * headers (<StudentHeaderIcons>). Mounted once by MainLayout, so every
 * redesigned student screen shares this one instance.
 *
 * The sheet shell (portal, backdrop, Escape, scroll lock, rounded top, drag
 * handle, safe-area padding) now lives in <BottomSheet>, shared with
 * <StudentNotificationsSheet>. This file keeps only the profile content; the
 * rationale for hand-rolling a bottom sheet instead of reusing BaseModal /
 * ContainedSideSheet is documented there.
 *
 * Deliberately does NOT render "Mon profil", "Paramètres" or "Aide & support":
 * no `/student/profile`, `/settings` or help route exists, and App.jsx's
 * catch-all is `<Navigate to="/login" replace />`, so such a row would log the
 * student out. Push notification settings are not here either: they are the
 * « Réglages » tab of <StudentNotificationsSheet> (the bell).
 *
 * Tap targets are 44px to match `.touch-target` (src/index.css).
 */
const StudentProfileSheet = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // No `if (!isOpen) return null` here: BottomSheet stays mounted and animates
  // its own exit — an early return would cut the closing slide short.
  const displayName = user?.name || user?.email || '';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'U';

  // Subtitle: the role only. The design reads "Élève · Groupe Streetlifting",
  // but no group/discipline value reaches the client — `profiles.discipline`
  // exists in the database yet `GET /api/auth/me` never selects it, so
  // `user.discipline` is always undefined here. Rendering a hardcoded group
  // would be inventing data, so the role stands alone until the field is
  // actually exposed.
  const roleKey = `student.home.profile_sheet.role_${user?.role || 'student'}`;
  const roleLabel = t(roleKey, {
    defaultValue: t('student.home.profile_sheet.role_student'),
  });

  const handleLogout = () => {
    // Déconnexion choisie par l'utilisateur : on désabonne aussi cet appareil du push.
    logout(false, '/login', { unsubscribePush: true });
    navigate('/login');
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="student-profile-sheet-name"
      testId="profile-sheet"
      backdropTestId="profile-sheet-backdrop"
    >
      {/* Header: avatar, name, role. No close button — the sheet closes by
          tapping the backdrop, by Escape, or by dragging it down. */}
      <div className="flex items-center gap-3 px-5 pt-2 pb-5">
        <div
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-kaiylo-accent text-[17px] font-bold text-white"
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p
            id="student-profile-sheet-name"
            className="truncate text-[17px] font-bold text-kaiylo-txt"
          >
            {displayName}
          </p>
          <p className="truncate text-[12px] text-kaiylo-txt-secondary">{roleLabel}</p>
        </div>
      </div>

      {/* Language */}
      <div className="flex flex-col gap-2 px-5 pb-5">
        <span className="text-[11px] uppercase tracking-wider text-kaiylo-txt-secondary">
          {t('common:settings.language_label')}
        </span>
        {/* Reused as-is: LanguageToggle already owns changeLanguage,
            localStorage and the Supabase write-back, and has its own tests.
            It styles itself with hardcoded inline styles and accepts no
            className/style prop, so its segments keep their own copper
            treatment rather than the design's copper gradient. */}
        <LanguageToggle />
      </div>

      {/* Logout */}
      <div className="px-5">
        <button
          type="button"
          onClick={handleLogout}
          data-testid="profile-sheet-logout"
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[14px] text-[15px] font-semibold text-red-400 transition-colors"
          style={{ background: 'rgba(239, 68, 68, 0.12)' }}
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
          <span>{t('common:nav.logout')}</span>
        </button>
      </div>
    </BottomSheet>
  );
};

export default StudentProfileSheet;
